import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { profile } from "../store.js";
import { openModal, spinner, toast, errorState } from "./ui.js";
import { HOME_TOOLS, localDay } from "../lib/training.js";

let fieldId = 0;
const field = (label, input) => {
  input.id ||= `plan-field-${++fieldId}`;
  return h("div", {class:"field"}, h("label", {for:input.id}, label), input);
};

export function editWeeklyPlan(onSaved = () => {}) {
  const root = h("div", null, spinner());
  const modal = openModal({content:root, title:"Your weekly plan"});
  api.trainingPlan().then(plan => {
    setChildren(root,
      h("p", {class:"dim"}, "Choose a target that fits your week. A paused week preserves continuity without earning a completed goal week. Program calendar dates stay as scheduled."),
      ...[["This week",plan],["Next week",plan.next_week]].map(([label,week]) => {
        const goal = h("input", {class:"input",type:"number",min:1,max:14,step:1,value:week.goal,required:true});
        const paused = h("input", {type:"checkbox",checked:!!week.paused});
        return h("form", {class:"plan-week",onSubmit:async e => {
          e.preventDefault(); const button=e.currentTarget.querySelector("button"); button.disabled=true;
          try { await api.updateTrainingWeek(week.week_start,{goal:Number(goal.value),paused:paused.checked}); toast(`${label} saved`,"success"); onSaved(); }
          catch(err) { toast(err.message,"error"); } finally { button.disabled=false; }
        }}, h("h3",null,`${label} · ${week.week_start}`),
          field("Workouts planned",goal),
          h("label",{class:"check-row"},paused,`Pause ${label.toLowerCase()}`),
          h("button",{class:"btn btn-primary",type:"submit"},`Save ${label.toLowerCase()}`));
      }),
      h("button",{class:"btn btn-ghost btn-block mt",onClick:()=>editNextSession(onSaved)},"Choose a date for my next session"),
      h("button",{class:"btn btn-ghost btn-block",onClick:modal.close},"Done"));
  }).catch(e => setChildren(root,errorState(e.message,()=>{modal.close();editWeeklyPlan(onSaved);}))); 
}

export function editNextSession(onSaved = () => {}, preferredWorkoutId = null) {
  const root = h("div",null,spinner());
  const modal = openModal({content:root,title:"Plan your next session"});
  Promise.all([api.trainingPlan(),api.workouts()]).then(([plan,workouts]) => {
    const intention=plan.intention;
    const date=h("input",{class:"input",type:"date",min:localDay(),value:intention?.scheduled_on || "",required:true});
    const workout=h("select",{class:"select"},h("option",{value:""},"Choose on the day"),
      ...workouts.map(w=>h("option",{value:w.id,selected:w.id===(intention?.workout_id || preferredWorkoutId)},w.name)));
    const cue=h("input",{class:"input",maxlength:80,value:intention?.cue || "",placeholder:"e.g. After work, at the gym"});
    setChildren(root,h("p",{class:"dim"},"Pick a date and an optional cue that fits your day. This adds a plan to Home. It doesn't turn on notifications."),
      h("form",{onSubmit:async e=>{
        e.preventDefault(); const button=e.currentTarget.querySelector("button");button.disabled=true;
        try { await api.saveTrainingIntention({scheduled_on:date.value,workout_id:workout.value?Number(workout.value):null,cue:cue.value}); modal.close();toast("Next session planned","success");onSaved(); }
        catch(err){toast(err.message,"error");button.disabled=false;}
      }},field("Date",date),field("Workout",workout),field("When or where (optional)",cue),h("button",{class:"btn btn-primary btn-block",type:"submit"},"Save next session")),
      intention?h("button",{class:"btn btn-ghost btn-block mt",onClick:async()=>{
        try{await api.clearTrainingIntention();modal.close();toast("Next-session plan cleared");onSaved();}catch(e){toast(e.message,"error");}
      }},"Clear next-session plan"):null);
  }).catch(e=>setChildren(root,errorState(e.message,()=>{modal.close();editNextSession(onSaved,preferredWorkoutId);}))); 
}

export function editHomeTools(onSaved = () => {}) {
  const selected=new Set(profile()?.home_tools || ["weight","measurements"]);
  const status=h("p",{class:"dim",role:"status"},"Choose up to two. You can also leave Home without shortcuts.");
  const checks=[];
  const root=h("div",null,status,...Object.entries(HOME_TOOLS).map(([key,[,name]])=>{
    const input=h("input",{type:"checkbox",checked:selected.has(key),onChange:()=>{
      if(input.checked && selected.size>=2){input.checked=false;status.textContent="Uncheck a shortcut before choosing another.";return;}
      input.checked?selected.add(key):selected.delete(key);
      status.textContent=`${selected.size} of 2 shortcuts selected.`;
    }});checks.push(input);return h("label",{class:"check-row"},input,name);
  }));
  const modal=openModal({content:root,title:"Home shortcuts"});
  root.append(h("button",{class:"btn btn-primary btn-block mt",onClick:async e=>{
    const button=e.currentTarget;button.disabled=true;
    try{profile(await api.updateMe({home_tools:[...selected]}));modal.close();onSaved();toast("Home shortcuts saved","success");}
    catch(err){toast(err.message,"error");button.disabled=false;}
  }},"Save shortcuts"));
}
